import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import defaultArt from '../assets/images/dj_kip_art.png';
import { usePlay } from '../context/Play';
import { useApi } from '../context/ApiProvider';
import { getImg } from '../utils/getImg';
import { Button, ButtonGroup } from '@mui/material';
import { PlayArrow, Pause } from '@mui/icons-material';
import ShareButton from './action_buttons/ShareButton';
import QueueButton from './action_buttons/QueueButton';
import MobileCard from './MobileCard';
import { useSnackbar } from 'notistack';
import { getSponsorDisplayText } from '../utils/sponsorUtils';

const SetMobileCard = ({ item, showGenreLink = true, extraSmall = false }) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    art_url_xl,
    artist,
    release_date,
    set_name,
    id,
    artists,
    set_sponsors,
    track_genres,
    user_fav,
  } = item;
  const [isFav, setIsFav] = useState(user_fav?.is_fav);
  const [showLikes, setShowLikes] = useState(user_fav?.show_likes);
  const api = useApi();

  // Memoize image URL to prevent flickering on re-renders
  const setImg = useMemo(() => getImg(art_url_xl) || defaultArt, [art_url_xl]);

  // Extract only the values we need from Play context and memoize them
  // This prevents re-renders when playingProgress updates (which happens every second)
  const playContext = usePlay();
  const isPlaying = playContext.isPlaying;
  const playingMetaId = playContext.playingMetaId;
  const audioRef = playContext.audioRef;
  const createAudioRef = playContext.createAudioRef;
  const playPause = playContext.playPause;

  // Memoize isSetPlaying to prevent unnecessary recalculations
  const isSetPlaying = useMemo(
    () => playingMetaId === item.id && isPlaying,
    [playingMetaId, item.id, isPlaying]
  );
  const releaseDateFormatted = release_date
    ? new Date(release_date).toLocaleDateString('en-US')
    : '';
  let navigate = useNavigate();

  // Truncate text for extra small version
  const truncateText = (text, maxLength = 25) => {
    if (!text) return text;
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  };

  const displayTitle = extraSmall ? truncateText(artist, 15) : artist;
  const displaySubtitle = extraSmall
    ? truncateText(set_name, 18)
    : truncateText(set_name, 30);

  // Create sponsor display text for mobile cards using utility function
  const sponsorDisplayText = getSponsorDisplayText(set_sponsors, {
    maxLength: extraSmall ? 15 : 25,
    showCount: true,
    extraSmall: extraSmall,
  });

  function goToSet() {
    navigate(`/sets/${item.id}`);
  }

  const handlePlayPauseClick = async () => {
    if (isSetPlaying || playingMetaId === item.id) {
      playPause(audioRef);
    } else {
      await createAudioRef(item.id);
    }
  };

  async function handleFavoriteClick() {
    const response = await api.put(`/favs/set/${item.id}`);
    if (response.ok) {
      setIsFav(response.body.is_fav);
      handleLikeNotification(response.body.is_fav, 'favorites');
    }
  }

  async function handleLikeClick() {
    const response = await api.put(`/favs/set/${item.id}/likes-toggle`);
    if (response.ok) {
      setShowLikes(response.body.show_likes);
      handleLikeNotification(response.body.show_likes, 'likes');
    }
  }

  function handleLikeNotification(showAdded, type) {
    if (showAdded) {
      enqueueSnackbar(`Set added to ${type}!`);
    } else {
      enqueueSnackbar(`Set removed from ${type}!`);
    }
  }

  function handleGenreClick(genre) {
    const encodedGenre = encodeURIComponent(genre.trim());
    navigate(`/sets?genre=${encodedGenre}`);
  }

  return (
    <MobileCard
      handleFavoriteClick={handleFavoriteClick}
      handleLikeClick={handleLikeClick}
      isFav={isFav}
      likes={user_fav?.user_likes}
      showLikes={showLikes}
      date={releaseDateFormatted}
      goToItem={goToSet}
      img={setImg}
      cardStyles={
        extraSmall
          ? {
              '& .MuiCardHeader-avatar': { display: 'none' },
              maxWidth: '200px',
              width: '200px',
              '& .MuiCardHeader-content': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              '& .MuiCardHeader-title': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              '& .MuiCardHeader-subheader': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }
          : {}
      }
      title={displayTitle}
      subtitle={displaySubtitle}
      subtitleExtra={sponsorDisplayText}
      chips={extraSmall || !showGenreLink ? [] : track_genres}
      onChipClick={extraSmall || !showGenreLink ? undefined : handleGenreClick}
    >
      {extraSmall ? (
        <ButtonGroup size="small" variant="contained">
          <Button
            variant="contained"
            color="primary"
            onClick={handlePlayPauseClick}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            {isSetPlaying ? <Pause /> : <PlayArrow />}
          </Button>
          <QueueButton setId={item.id} size="small" />
          <ShareButton
            id={item.id}
            artistName={artist || artists?.[0]?.artist_meta?.name}
            size="small"
          />
        </ButtonGroup>
      ) : (
        <ButtonGroup size="large" variant="contained">
          <Button
            variant="contained"
            color="primary"
            onClick={handlePlayPauseClick}
            startIcon={isSetPlaying ? <Pause /> : <PlayArrow />}
          >
            {isSetPlaying ? 'Pause' : 'Play'}
          </Button>
          <QueueButton setId={item.id} />
          <ShareButton
            id={item.id}
            artistName={artist || artists?.[0]?.artist_meta?.name}
          />
        </ButtonGroup>
      )}
    </MobileCard>
  );
};

SetMobileCard.propTypes = {
  item: PropTypes.object.isRequired,
  showGenreLink: PropTypes.bool,
  extraSmall: PropTypes.bool,
};

export default SetMobileCard;
